import React from 'react';
import PropTypes from 'prop-types';

import { openReport } from '../../Utilities/openReport';

const ExternalLink = ({ link, text }) => (
  <a style={{ color: '#0101b7' }} target='_blank' rel='noreferrer' href={link}>
    {text || link}
  </a>
);
ExternalLink.propTypes = {
  link: PropTypes.string,
  text: PropTypes.string,
};

const InternalLink = ({ modalID, text, selectLinkFunc }) => (
  <a
    className='local-link'
    target='_blank'
    rel='noreferrer'
    onClick={(e) => selectLinkFunc(e, modalID)}
  >
    {text || modalID}
  </a>
);
InternalLink.propTypes = {
  modalID: PropTypes.string,
  text: PropTypes.string,
  selectLinkFunc: PropTypes.func,
};

export const modalText = (selectLinkFunc) => ({
  howToUseTool: {
    title: 'How to Use the Tool',
    lists: [
      {
        heading: 'Getting Oriented in the Tool',
        items: [
          'When entering the tool, users will see a map of the U.S. along with the highlighted states for which CHaMP currently has data: New York, New Jersey, Pennsylvania, Maryland, Delaware, Washington D.C., Virginia, North Carolina, South Carolina, West Virginia, Ohio, Indiana, Illinois, Michigan, Wisconsin, and Minnesota.',
          'There are several panels visible from the homepage along the outer edges of the map. Along the top of the map is the selection panel and time range selector. These allow the user to select the climate and hazard information that they wish to visualize and the time period that is relevant to their analysis. More information about how to use the selection panel and time selector is included in the next section.',
          'On the left side of the homepage is a white panel, which has information about how to get started using the CHaMP tool. The panel on the left side of the page is also where interpretive text describing plots and tends will appear once the user selects a climate metric or hazard.',
          'On the right side of the homepage, there is a circular icon and a question mark icon. Users can click on the circular icon to search for a particular location. This search will only provide results for locations within the states for which CHaMP currently has data. Clicking on the question mark icon will bring up a menu where users can find additional documentation on the purpose of the tool, how to use the tool, and data and technical information.',
          'A legend and zoom controls can be found at the bottom right corner of the homepage.',
        ],
      },
      {
        heading: 'To adjust the time range:',
        items: [
          'In the Time Range selector (located at top right of the map), use the slider bars to select the beginning and end years of interest. The range is initially set as 1981 to 2021 but could be constrained to a shorter time interval (e.g., 20 years rather than 30 years). The tool only allows users to select a minimum period of 20 years, as shorter time periods could provide inaccurate long-terms trends because of the magnitude of annual variability.',
        ],
      },
      {
        heading: 'To select climate hazards and metrics:',
        items: [
          'From the options panel at the top of the map, use the first drop down menu labeled “Theme” to select a category of climate hazards. This will populate a list in the Climate Metric/Hazard Type drop down menu that includes a list of available climate metrics and/or hazard types. Users can then choose from this list. If the user has selected a hazard type, additional metrics can be selected under the drop down labeled “Impacts”. Once either a climate metric or an impact has been selected, it will be shown on the map below. A full list of the climate metrics and hazards by theme is included in the table below.',
          'hmTable',
        ],
      },
      {
        heading: 'To view data at state and county levels:',
        items: [
          'Another option for viewing climate and hazard data in CHaMP is to zoom in on a particular state or county. Once a hazard type or metric is selected in the options panel, the map will initially display data for this hazard or metric aggregated at the state level. Users can then click a specific state on the map. The tool will then zoom in on that state with county-level trends shown on the map and a written summary of state-level trends shown in the left side panel. Users can select a county by clicking it on the map. In the county view, the left side panel updates to describe county-level trends.',
        ],
      },
      {
        heading: 'Viewing Climate Data and Plots:',
        items: [
          'Once a climate metric is selected and the user has zoomed in on either a state or a county, the left side panel will show two buttons along the bottom – SHOW HISTORICAL DATA and SHOW PROJECTED DATA.',
          'In order to see a plot of the annual historical observational data used to determine the trends described in the text in the left side panel, users can select the SHOW HISTORICAL DATA button to view data on state- or county-level historical climate metrics as a time series.',
          'Future climate projections can be viewed either by selecting the SHOW PROJECTED DATA button in the left side panel, or if the user has already selected the historical data and is viewing the chart, by using the tabs at the top of the chart to select one of two future greenhouse gas emissions scenarios (see the Data and Technical Information or the CHaMP report for more details). Users may then select HIGH EMISSION FUTURE or LOW EMISSION FUTURE to view future climate projections for the metric of interest for either of these two future greenhouse gas emissions scenarios.',
          'When viewing future projected data, the left side panel text will update to show a description of the trends seen in the future projections. Information about the source of the climate projections and guidance about how to use climate projections are also provided in the left side panel.',
        ],
      },
      {
        heading: 'Viewing Hazard Data and Plots:',
        items: [
          'Users can also view hazard data when zoomed into the state- or county-level. The left side panel provides a summary of the number of events and their impacts in terms of reported property damage, reported crop damage, reported deaths, and reported injuries. The side panel also provides a citation for the source of the hazard data, the NOAA NCEI Storm Events Database.',
          'From this view, the user can select the SHOW ANNUAL DATA button at the bottom of the left side panel to view the time series for the selected hazard data.',
          'It is possible for the user to get more information about specific hazard events by clicking on one of the bars in the bar chart of the annual data. This will cause the left side panel to update and to show the dates of specific hazard events as well as any narrative information that was reported to the NOAA NCEI Storm Events Database.',
          'Users should be aware that not all events in the Storm Events Database have detailed, or any, narratives. However, when available, these narratives can provide quite detailed information on the impacts of the particular event such as amount of rainfall, specific roads impacted, etc.',
        ],
      },
      {
        heading: 'To copy, paste, print, and download charts/text/data:',
        items: [
          'Users can copy and paste descriptive text from any part of the tool for use in climate- or hazard-related plans, public education materials, and other documents.',
          'Charts can be downloaded in a variety of formats by clicking on the 3-barred icon (“hamburger”) on the upper right corner, just below the X that is used to exit out of the chart. Users can also download the data in a .csv file by selecting the same 3-barred icon.',
          <>
            <span>
              If you have any additional data needs, please share these with the
              CHaMP team via{' '}
            </span>
            <ExternalLink link='mailto:CHaMP@rand.org' text='CHaMP@rand.org' />
            <span>
              {' '}
              so we can incorporate them into a future version of the CHaMP
              tool.
            </span>
          </>,
        ],
      },
      {
        heading: 'To view source documents:',
        items: [
          'Sources are cited throughout the CHaMP tool via hyperlinked text, which can be identified via underlining, and via footnotes. Clicking on either the hyperlinked text or the footnote will take you directly to the source document that the CHaMP team is citing.',
        ],
      },
    ],
  },
  purposeOfTool: {
    title: 'Purpose of the Tool',
    lists: [
      {
        items: [
          'The Climate Hazard and Mitigation Planning (CHaMP) tool is designed to communicate potential vulnerabilities to residents and authorities responsible for allocating resources for mitigation activities; and to help local hazard mitigation, infrastructure, and land use planners integrate climate information into their hazard mitigation planning efforts. CHaMP provides users with a single point of access to county- and region-specific historical climate and hazard data and future projected climate information. The tool displays these climate metrics, hazard data, and hazard impacts in a visual format with downloadable data visualizations and tables that are accompanied by explanatory text. CHaMP is intended to be a timely and durable resource for historical and future climate information linked to natural hazard data.',
          <>
            <span>
              In CHaMP, climate data are metrics that relate to historical and
              future temperature and precipitation such as “days with
              precipitation greater than 1 inch” or “highest summer temperature”
              while hazard refers to specific storm or other significant weather
              events that have the potential to “cause loss of life, injuries,
              significant property damage, and/or disruption to commerce” as
              documented in the{' '}
            </span>
            <ExternalLink
              link='https://www.ncdc.noaa.gov/stormevents/'
              text='NOAA National Centers for Environmental Information (NCEI) Storm Events Database'
            />
            <span>
              . A complete list of the climate metrics and hazard events
              included in the tool is provided in the{' '}
            </span>
            <InternalLink
              modalID='howToUseTool'
              text='How to Use the Tool'
              selectLinkFunc={selectLinkFunc}
            />
            <span>
              {' '}
              tab and in Chapter 3 of the Climate Hazard and Mitigation Planning
              (CHaMP) Tool: Background and Guidance for Users Report.
            </span>
          </>,
        ],
      },
      {
        heading: 'Intended audience',
        items: [
          'Public officials, including but not limited to planners, emergency managers, engineers, consultants, other climate service providers, and community leaders and members interested in integrating climate, hazard, and impacts data and information into planning, public education, and other documents.',
          <>
            <span>
              State, local, tribal and territorial government officials leading
              or contributing to Hazard Mitigation Plans that serve as an
              eligibility requirement for U.S. Federal Emergency Management
              Agency (
            </span>
            <ExternalLink link='https://www.fema.gov/' text='FEMA' />
            <span>) disaster mitigation grants.</span>
          </>,
        ],
      },
      {
        heading: 'Key Features of CHaMP',
        items: [
          <>
            <span>
              This tool was designed, via a co-production process with the Urban
              Sustainability Directors’ Network (
            </span>
            <ExternalLink
              link='https://www.usdn.org/index.html#/'
              text='USDN'
            />
            <span>) and </span>
            <ExternalLink
              link='https://cpo.noaa.gov/Meet-the-Divisions/Climate-and-Societal-Interactions/RISA'
              text='RISA'
            />
            <span>
              {' '}
              community partners, specifically to address the substantive and
              communications needs of individuals developing hazard mitigation
              plans as required by{' '}
            </span>
            <ExternalLink link='https://www.fema.gov/' text='FEMA' />
            <span>.</span>
          </>,
          <>
            <span>
              Observational data in CHaMP are current and will stay current as
              they are linked to databases at the Northeast Regional Climate
              Center (NRCC) that are updated on a monthly basis (see the{' '}
            </span>
            <InternalLink
              modalID='dataAndTech'
              text='Data and Technical Information'
              selectLinkFunc={selectLinkFunc}
            />
            <span> tab for more information).</span>
          </>,
          'CHaMP presents a broad range of data that go beyond temperature and precipitation by providing information on a wider range of hazards interest to planners, such as severe weather events and wildfire.',
          'CHaMP also provides regionally relevant information on hazards such as those specific to the Great Lakes or to coastal regions.',
          'CHaMP includes interpretive text that is customized for each county and includes descriptions of trends. This text can be included directly into hazard mitigation plans via copy/paste.',
        ],
      },
    ],
  },
  dataAndTech: {
    title: 'Data and Technical Information',
    lists: [
      {
        heading: 'CHaMP Report and Citation',
        items: [
          <>
            <span>
              A report that provides additional details on the data and methods
              used in developing the CHaMP tool can be accessed here:
            </span>
            <div style={{ marginTop: '8px', textAlign: 'center' }}>
              <InternalLink
                modalID='https://www.rand.org/pubs/tools/TLA386-9.html'
                selectLinkFunc={openReport}
              />
            </div>
          </>,
          <>
            <div>The citation for CHaMP is:</div>
            <div style={{ margin: '8px 44px', fontSize: '12px' }}>
              <cite>
                Romita Grocholski, Krista, Arthur T. DeGaetano, Benjamin Eck,
                Debra Knopman, Kirstin Dow, Lena Easton-Calabria, Amanda Farris,
                Jory Fleming, Omar Gates, Jenna Jorns, and Michelle E. Miro,
                Climate Hazard and Mitigation Planning (CHaMP) Tool: Background
                and Guidance for Users. Santa Monica, CA: RAND Corporation,
                2022. https://www.rand.org/pubs/tools/TLA386-9.html
              </cite>
            </div>
          </>,
        ],
      },
      {
        heading: 'Climate Data',
        items: [
          <>
            <span>
              The climate data provided in the CHaMP tool comes from the Applied
              Climate Information System (
            </span>
            <ExternalLink
              link='https://www.rcc-acis.org/index.html'
              text='ACIS'
            />
            <span>) through the Northeast Regional Climate Center (</span>
            <ExternalLink link='http://www.nrcc.cornell.edu/' text='NRCC' />
            <span>). </span>
            <ExternalLink
              link='https://www.rcc-acis.org/index.html'
              text='ACIS'
            />
            <span>
              {' '}
              is “a system that delivers operational information derived from
              historical archives and near real-time climate data.” The climate
              data that are “available in{' '}
            </span>
            <ExternalLink
              link='https://www.rcc-acis.org/index.html'
              text='ACIS'
            />
            <span>
              {' '}
              consist of in-situ observations collected from a variety of
              federal, regional, state, and local networks.”{' '}
            </span>
            <ExternalLink
              link='https://www.rcc-acis.org/index.html'
              text='ACIS'
            />{' '}
            also includes gridded datasets, such as regionally downscaled
            climate model outputs and daily and monthly gridded historical data.
            Additional information on{' '}
            <ExternalLink
              link='https://www.rcc-acis.org/index.html'
              text='ACIS'
            />
            <span>can be found </span>
            <ExternalLink
              link='https://www.rcc-acis.org/aboutacis_overview.html'
              text='here'
            />
            <span>.</span>
          </>,
          <>
            <span>
              The historical climate data that is used in the CHaMP tool are
              pulled from the ACIS databases and originates the{' '}
            </span>
            <ExternalLink
              link='https:/prism.oregonstate.edu'
              text='Parameter-evaluation Regressions on Independent Slopes Model (PRISM) Spatial Climate Datasets'
            />
            <span>, specifically from their </span>
            <ExternalLink
              link='https://prism.oregonstate.edu/recent/'
              text='“recent years” gridded dataset'
            />
            <span>
              . This dataset provides climatological data (e.g. precipitation
              and temperature) from January 1981 to December 2021 and is updated
              annually.
            </span>
          </>,
          <>
            <span>
              The future projections in the CHaMP tool are drawn from{' '}
            </span>
            <ExternalLink
              link='https://www.rcc-acis.org/index.html'
              text='ACIS'
            />
            <span>
              {' '}
              and originate from the Localized Constructed Analogs (LOCA). LOCA
              is a statistically downscaled climate data product with 6km
              resolution over the entire continental United States and includes
              all 32 climate models from the Coupled Model Intercomparison
              Project 5 (CMIP5) archive. The low emissions future projections in
              the tool are the LOCA results for{' '}
            </span>
            <ExternalLink
              link='https://link.springer.com/article/10.1007/s10584-011-0151-4'
              text='Representative Concentration Pathway (RCP) 4.5'
            />
            <span>
              {' '}
              and the high emission future projections are the results from LOCA
              for{' '}
            </span>
            <ExternalLink
              link='https://link.springer.com/article/10.1007%2Fs10584-011-0149-y'
              text='RCP 8.5'
            />
            <span>
              . Both RCPs were developed as inputs for climate models leading up
              to the{' '}
            </span>
            <ExternalLink
              link='https://www.ipcc.ch/assessment-report/ar5/'
              text='Intergovernmental Panel on Climate Change’s (IPCC’s) Fifth Assessment Report'
            />
            <span>
              . The RCPs “describe future pathways of emissions and
              concentrations of greenhouse gases, aerosols, chemically active
              gases, and land use/cover change.”
            </span>
          </>,
          'Climate model data are best used to look at the overall amount and rate of change of climate metrics, such as average annual temperature and annual precipitation totals, over time rather than as a forecast for a single year. The climate model scenarios (i.e., RCPs) are representations of future concentrations of greenhouse gases, aerosols, other gases, and changes in land use/cover. The data represented in these charts show the range of possible climate changes from 32 different climate models that were each run under the same representative concentration pathway.  No single model is a best estimate of the future, therefore it is important to consider the full range of model results, rather than a single projection, to understand what your region/state/county could expect to see due to a changing climate. Additionally, actual future conditions may be outside of the range of the model data.',
          <>
            <span>
              Users in the Great Lakes region who are concerned with using
              future climate projections that specifically account for the lakes
              themselves (i.e., lake-land-atmosphere interactions) should refer
              to the{' '}
            </span>
            <ExternalLink
              link='https://glisa.umich.edu/project/great-lakes-ensemble/'
              text='Great Lakes RISA team’s Ensemble'
            />
            <span> or email </span>
            <ExternalLink
              link='mailto: glisa-info@umich.edu'
              text='glisa-info@umich.edu'
            />
            <span> for more information).</span>
          </>,
          <>
            <span>
              Additional information about the LOCA datasets and methodology can
              be found{' '}
            </span>
            <ExternalLink link='http://loca.ucsd.edu/' text='here' />
            <span>.</span>
          </>,
          <>
            <span>
              A practitioner’s guide to climate model scenarios, including RCPs,
              can be found{' '}
            </span>
            <ExternalLink
              link='https://glisa.umich.edu/wp-content/uploads/2021/03/A_Practitioners_Guide_to_Climate_Model_Scenarios.pdf'
              text='here'
            />
            <span>.</span>
          </>,
        ],
      },
      {
        heading: 'Hazard and Impacts Data',
        items: [
          <>
            <span>
              The data on climate hazards and impacts come from the National
              Oceanic and Atmospheric Administration (
            </span>
            <ExternalLink link='https://www.noaa.gov/' text='NOAA' />
            <span>) National Centers for Environmental Information (</span>
            <ExternalLink link='https://www.ncei.noaa.gov/' text='NCEI' />
            <span>) </span>
            <ExternalLink
              link='https://www.ncdc.noaa.gov/stormevents/'
              text='Storm Events Database'
            />
            <span>
              . This database “is an official publication of [...] NOAA which
              documents the occurrence of storms and other significant weather
              phenomena having sufficient intensity to cause loss of life,
              injuries, significant property damage, and/or disruption to
              commerce. In addition, it is a partial record of other significant
              meteorological events.” The database contains information, as
              entered by personnel from the National Weather Service (
            </span>
            <ExternalLink link='https://www.weather.gov/' text='NWS' />
            <span>
              ), on 48 different event types from 1996 to the present.
            </span>
          </>,
          <>
            <span>
              The CHaMP tool provides data on a subset of the 48 event types
              tracked by the{' '}
            </span>
            <ExternalLink link='https://www.weather.gov/' text='NWS' />
            <span> and included in the </span>
            <ExternalLink
              link='https://www.ncdc.noaa.gov/stormevents/'
              text='Storm Events Database'
            />
            <span>
              . All of the events tracked in the database are defined and
              detailed in{' '}
            </span>
            <ExternalLink
              link='https://www.nws.noaa.gov/directives/sym/pd01016005curr.pdf'
              text='NWS Directive 10-1605'
            />
            <span>
              . We have included summarized versions of these definitions in the
              text accompanying the Hazard and Impacts data within the tool. A
              table with definitions is also included in the CHaMP report.
            </span>
          </>,
          <>
            <span>Please note that the </span>
            <ExternalLink link='https://www.weather.gov/' text='NWS' />
            <span>
              {' '}
              does not guarantee the accuracy, validity, or completeness of the
              event data in the{' '}
            </span>
            <ExternalLink
              link='https://www.ncdc.noaa.gov/stormevents/'
              text='Storm Events Database'
            />
            <span>
              . This is because some of the information included in the database
              has been collected/reported by non-NWS personnel (i.e., government
              agencies, the media, individuals, etc.).{' '}
            </span>
            <ExternalLink link='https://www.weather.gov/' text='NWS' />
            <span>
              {' '}
              is unable to validate all of the data collected from these kinds
              of sources due to resource and time constraints.
            </span>
          </>,
          <>
            <span>
              If you have additional event data that you would like to see
              included in the CHaMP tool, please email{' '}
            </span>
            <ExternalLink link='mailto: CHaMP@rand.org' text='CHaMP@rand.org' />
            <span> with details.</span>
          </>,
        ],
      },
      {
        heading: 'Limitations',
        items: [
          'The CHaMP team has conducted extensive testing with the Storm Events Database throughout the development of the online tool. There are significant limitations that we have observed, particularly the incomplete and inconsistent reporting of hazard events and impacts. For example, one coastal county can report that it was significantly impacted by a tropical storm event and a neighboring coastal county may have no reported data from that same storm event, despite the storm having passed over that county. An additional limitation of this database is that it only provides data from 1996 to the present. This is a relatively limited time period and therefore, the database may not contain information on rare but potentially impactful hazards.',
          'Therefore, the CHaMP team recommends using the hazard and impacts data within CHaMP with caution and to supplement it with additional local or state level databases whenever possible.',
        ],
      },
      {
        heading: 'Questions and Feedback',
        items: [
          <>
            <span>
              For questions or feedback on the current version of the CHaMP
              tool, or for suggestions on what features would be helpful to
              include in the next version of the tool, please reach out by
              emailing{' '}
            </span>
            <ExternalLink link='mailto: CHaMP@rand.org' text='CHaMP@rand.org' />
            <span>.</span>
          </>,
        ],
      },
    ],
  },
});
